<template>
  <div>
    <ClientOnly>
      <AddressOpeningHoursStatus v-if="nid" :nid="nid" />
    </ClientOnly>
    <slot v-if="!hasStructuredOpeningHours" />
    <div v-if="currentlyClosed">
      {{ $texts('currentlyClosed') }}
    </div>
    <div v-else-if="seasonallyClosed">{{ seasonallyClosed }}</div>
    <div v-else-if="hasStructuredOpeningHours || custom">
      <OpeningHours v-if="hasStructuredOpeningHours" :days />
      <div
        v-if="custom"
        class="address-opening-hours-custom content"
        v-html="custom"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { AddressOpeningHoursFragment } from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import { formatShortDate } from '~/helpers'

const { $texts } = useEasyTexts()
const currentLanguage = useCurrentLanguage()

const props = withDefaults(
  defineProps<{
    custom?: AddressOpeningHoursFragment['custom']
    structured?: AddressOpeningHoursFragment['structured']
    closedFrom?: AddressOpeningHoursFragment['closedFrom']
    closedUntil?: AddressOpeningHoursFragment['closedUntil']
    currentlyClosed?: AddressOpeningHoursFragment['currentlyClosed']
    alwaysOpen?: boolean
    nid?: string
  }>(),
  {
    custom: undefined,
    structured: () => [],
    closedFrom: undefined,
    closedUntil: undefined,
    currentlyClosed: false,
    alwaysOpen: false,
    nid: undefined,
  },
)

const days = computed(() => {
  return props.structured
    .map((v) => {
      if (v && notNullish(v.start) && notNullish(v.end) && notNullish(v.day)) {
        return {
          start: v.start,
          end: v.end,
          day: v.day,
          comment: v.comment,
        }
      }
      return null
    })
    .filter(notNullish)
})

/**
 * Determine if the current date is in between the seasonally closed dates.
 * If true, return the appropiate string to display.
 */
function isSeasonallyClosed(
  closedFrom?: string,
  closedUntil?: string,
): string | false {
  if (!closedFrom && !closedUntil) {
    return false
  }

  const now = new Date().setHours(0, 0, 0, 0)
  const from = closedFrom ? new Date(closedFrom).setHours(0, 0, 0, 0) : null
  const until = closedUntil ? new Date(closedUntil).setHours(0, 0, 0, 0) : null

  let result

  if (from && until) {
    if (now >= from && now <= until) {
      result = $texts('seasonallyClosed')
    }
  } else if (from) {
    if (now >= from) {
      result = $texts('seasonallyClosedFrom')
    }
  } else if (until && now <= until) {
    result = $texts('seasonallyClosedUntil')
  }

  if (result) {
    return formatSeasonallyClosed(result, from, until)
  }

  return false
}

const hasStructuredOpeningHours = computed(() => !!props.structured?.length)

const seasonallyClosed = computed(() => {
  const closedFrom = props.closedFrom?.date
  const closedUntil = props.closedUntil?.date
  return isSeasonallyClosed(closedFrom, closedUntil)
})

// Helper function migrated from methods
function formatSeasonallyClosed(
  text: string,
  from?: Date | null | number,
  until?: Date | number | null,
) {
  let replaced = text
  if (from) {
    replaced = replaced.replace(
      '@from',
      formatShortDate(new Date(from), currentLanguage.value),
    )
  }
  if (until) {
    replaced = replaced.replace(
      '@until',
      formatShortDate(new Date(until), currentLanguage.value),
    )
  }

  return replaced
}
</script>

<style lang="postcss">
.detail-layout-section {
  .opening-hours-status {
    @apply mb-[1.5rem];
    @apply tablet:absolute tablet:right-0 tablet:top-[1.5rem] tablet:font-bold;
    @apply desktop:top-[2rem];
  }
}

.address-opening-hours-custom {
  @apply pt-size-5;
}
</style>
